import React from "react";
import { TChunkStatus } from "../types/db";
import { Button, Progress } from "antd";
import { AlertTriangle, Upload, Download, Trash2, HardDrive } from "lucide-react";
import "./StuckChunksOverlay.scss";

interface StuckChunk {
  chunkNo: number;
  status: TChunkStatus;
  size: number;
  createdAt: number;
}

interface StuckChunksData {
  videoGuid: string;
  uploadId?: string;
  key?: string;
  chunks: StuckChunk[];
}

interface DownloadProgress {
  current: number;
  total: number;
  currentVideoGuid: string;
}

interface StuckChunksOverlayProps {
  stuckChunks: StuckChunksData[];
  totalStuckChunks: number;
  isProcessingStuckChunks: boolean;
  isDownloadingAndDiscarding: boolean;
  downloadProgress: DownloadProgress | null;
  onDiscardStuckChunks: () => void;
  onRetryStuckChunks: () => void;
  handleDownloadAndDiscard: () => void;
}

function StuckChunksOverlay({
  stuckChunks,
  totalStuckChunks,
  isProcessingStuckChunks,
  isDownloadingAndDiscarding,
  downloadProgress,
  onDiscardStuckChunks,
  onRetryStuckChunks,
  handleDownloadAndDiscard,
}: StuckChunksOverlayProps) {
  const progressPercent = downloadProgress 
    ? Math.round((downloadProgress.current / downloadProgress.total) * 100) 
    : 0;

  const isDisabled = isProcessingStuckChunks || isDownloadingAndDiscarding;

  return (
    <div className="stuck-chunks-overlay">
      <div className="overlay-card">
        <div className="card-header">
          <div className="icon-wrapper warning">
            <AlertTriangle size={20} />
          </div>
          <div className="header-text">
            <h3 className="title">Unfinished Upload Found</h3>
            <p className="subtitle">Your last recording didn't finish uploading</p>
          </div>
        </div>

        <div className="card-body">
          <div className="info-row">
            <div className="info-icon">
              <HardDrive size={14} />
            </div>
            <span className="info-text">
              {stuckChunks.length} recording{stuckChunks.length !== 1 ? 's' : ''} waiting to be uploaded
            </span>
          </div>

          {isDownloadingAndDiscarding && downloadProgress && (
            <div className="progress-section">
              <div className="progress-header">
                <span className="progress-label">Saving recordings locally...</span>
                <span className="progress-count">
                  {downloadProgress.current} of {downloadProgress.total}
                </span>
              </div>
              <Progress 
                percent={progressPercent} 
                showInfo={false}
                strokeColor="#4A3CFA"
                trailColor="rgba(74, 60, 250, 0.12)"
                size="small"
              />
            </div>
          )}

          <p className="question-text">What would you like to do?</p>
        </div>

        <div className="card-actions">
          <Button
            type="primary"
            className="action-btn primary"
            onClick={onRetryStuckChunks}
            disabled={isDisabled}
            loading={isProcessingStuckChunks && !isDownloadingAndDiscarding}
            icon={!isProcessingStuckChunks ? <Upload size={16} /> : undefined}
          >
            Resume Upload
          </Button>
          <Button
            className="action-btn secondary"
            onClick={handleDownloadAndDiscard}
            disabled={isDisabled}
            loading={isDownloadingAndDiscarding}
            icon={!isDownloadingAndDiscarding ? <Download size={16} /> : undefined}
          >
            {isDownloadingAndDiscarding
              ? downloadProgress
                ? `Saving ${downloadProgress.current}/${downloadProgress.total}...`
                : "Saving..."
              : "Save Copy & Remove"}
          </Button>
          <Button
            danger
            className="action-btn danger"
            onClick={onDiscardStuckChunks}
            disabled={isDisabled}
            loading={isProcessingStuckChunks && !isDownloadingAndDiscarding}
            icon={!isProcessingStuckChunks ? <Trash2 size={16} /> : undefined}
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
}

export default StuckChunksOverlay;
export type { StuckChunksOverlayProps, StuckChunksData, DownloadProgress };
